/**
 * Publish Template Dialog
 * 
 * Confirmation dialog for publishing a draft template with change notes
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2, Upload, AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface PublishTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateId: string;
  templateName: string;
  version: number;
  onPublish: (changeNotes?: string) => Promise<void>;
  isPublishing: boolean;
}

export function PublishTemplateDialog({
  open,
  onOpenChange,
  templateId,
  templateName,
  version,
  onPublish,
  isPublishing,
}: PublishTemplateDialogProps) {
  const [changeNotes, setChangeNotes] = useState('');

  const handlePublish = async () => {
    await onPublish(changeNotes || undefined);
    setChangeNotes('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Publish Template</DialogTitle>
          <DialogDescription>
            Publish version {version} of "{templateName}"
          </DialogDescription>
        </DialogHeader>

        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Publishing will make this the active template for new inspections.
            Existing inspections will continue to use their original template version.
          </AlertDescription>
        </Alert>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="changeNotes">Change Notes (optional)</Label>
            <Textarea
              id="changeNotes"
              value={changeNotes}
              onChange={(e) => setChangeNotes(e.target.value)}
              placeholder="Describe what changed in this version..."
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              These notes will be saved in the version history.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPublishing}
          >
            Cancel
          </Button>
          <Button onClick={handlePublish} disabled={isPublishing}>
            {isPublishing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Upload className="h-4 w-4 mr-2" />
            )}
            Publish
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
